import { morph } from '@theme/morph';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Horizon Pro — product page behaviour for the Pro product section (sections/main-product.liquid).
 *
 * Variant changes go through Horizon's <variant-picker>, which dispatches the standard ProductSelectEvent
 * with the new page HTML. The elements below listen to that event and update themselves from it.
 */

const STICKY_VISIBLE_CLASS = 'pro-sticky-atc-visible';

/**
 * Subscribes to ProductSelectEvent on the closest section and returns an unsubscribe function.
 * @param {HTMLElement} element
 * @param {(detail: any) => void} callback - Called with the resolved event detail (html, resource, ...)
 */
export function onProductSelect(element, callback) {
  const section = element.closest('.shopify-section');
  if (!section) return () => {};

  /** @param {any} event */
  const listener = (event) => {
    if (!event.promise) return;
    event.promise
      .then((/** @type {any} */ result) => {
        if (result?.detail?.html) callback(result.detail);
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[pro] variant update failed', error);
      });
  };

  section.addEventListener(StandardEvents.productSelect, listener);
  return () => section.removeEventListener(StandardEvents.productSelect, listener);
}

/** URL of the checkout, respecting the market / language root */
export function checkoutUrl() {
  const root = window.Shopify?.routes?.root || '/';
  return `${root.endsWith('/') ? root : `${root}/`}checkout`;
}

/**
 * Adds several items to the cart and updates Horizon's cart drawer and cart count
 * (the drawer opens by itself, as with the regular Add to cart button).
 * @param {Array<{id: number | string, quantity?: number, properties?: Record<string, string>}>} items
 * @param {{ source?: Element, checkout?: boolean }} [options]
 * @returns {Promise<any>} The /cart/add.js response
 */
export async function proAddToCart(items, { source = document.body, checkout = false } = {}) {
  const lines = items.filter((item) => item.id).map((item) => ({ ...item, quantity: item.quantity || 1 }));
  if (lines.length === 0) throw new Error('Nothing to add');

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  if (!checkout) {
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );
  }

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');

    if (checkout) {
      window.location.href = checkoutUrl();
      return data;
    }

    const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        sections: data.sections,
        itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
        source: 'pro-add-to-cart',
        sourceId: 'pro-add-to-cart',
        didError: false,
      },
    });
    return data;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Re-renders the cart drawer and cart count after cart changes made outside proAddToCart.
 * @param {Element} [source]
 */
export async function proRefreshCart(source = document.body) {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise }));
  try {
    const [cart, sections] = await Promise.all([
      fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
      sectionIds.length
        ? fetch(`${Theme.routes.cart_url}?sections=${sectionIds.join(',')}`).then((r) => r.json())
        : Promise.resolve({}),
    ]);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections, source: 'pro-gifts', sourceId: 'pro-gifts', didError: false },
    });
  } catch (error) {
    deferred.reject(error);
  }
}

/**
 * Re-renders its children from the server HTML of the newly selected variant.
 * Match is done by data-sync-id, so every <pro-sync> needs a unique one.
 */
class ProSync extends HTMLElement {
  /** @type {() => void} */
  #unsubscribe = () => {};

  connectedCallback() {
    this.#unsubscribe = onProductSelect(this, ({ html }) => {
      const id = this.dataset.syncId;
      if (!id) return;
      const fresh = html.querySelector(`pro-sync[data-sync-id="${CSS.escape(id)}"]`);
      if (fresh) {
        morph(this, fresh);
        window.ProIcons?.refresh();
      }
    });
  }

  disconnectedCallback() {
    this.#unsubscribe();
  }
}

/**
 * Wraps Horizon's <variant-picker> and, when "skip sold out variants" is on, adjusts the other options
 * to the closest available variant before the picker requests the new variant.
 * Also exposes selectOptions() for the sticky Add to cart dropdowns.
 */
class ProVariantPicker extends HTMLElement {
  /** @type {Array<{id: number, available: boolean, options: string[]}> | null} */
  #variants = null;

  connectedCallback() {
    this.addEventListener('change', this.#onChange, true);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange, true);
  }

  get variants() {
    if (!this.#variants) {
      const script = this.querySelector('script[data-pro-variants]');
      try {
        this.#variants = JSON.parse(script?.textContent || '[]');
      } catch {
        this.#variants = [];
      }
    }
    return this.#variants ?? [];
  }

  /** @returns {string[]} Selected value per option position (index 0 = option 1) */
  getSelectedValues() {
    /** @type {string[]} */
    const values = [];
    for (const container of this.querySelectorAll('[data-option-position]:is(fieldset, .pro-option)')) {
      const position = Number(/** @type {HTMLElement} */ (container).dataset.optionPosition);
      const select = container.querySelector('select');
      const checked = /** @type {HTMLInputElement | null} */ (container.querySelector('input[type="radio"]:checked'));
      const value = select ? select.value : checked?.value;
      if (position && value !== undefined) values[position - 1] = value;
    }
    return values;
  }

  /**
   * Sets an option value in the picker without notifying it.
   * @param {number} position - Option position (1-based)
   * @param {string} value
   * @returns {HTMLInputElement | HTMLSelectElement | null} The changed control
   */
  setOptionValue(position, value) {
    const container = this.querySelector(`[data-option-position="${position}"]:is(fieldset, .pro-option)`);
    if (!container) return null;

    const select = container.querySelector('select');
    if (select) {
      for (const option of select.options) {
        if (option.value === value) {
          option.setAttribute('selected', 'selected');
          option.selected = true;
        } else {
          option.removeAttribute('selected');
        }
      }
      return select;
    }

    for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (container.querySelectorAll('input[type="radio"]'))) {
      if (input.value === value) {
        input.checked = true;
        return input;
      }
    }
    return null;
  }

  /**
   * Selects all option values at once and lets the variant picker load the variant.
   * @param {string[]} values - Value per option position
   */
  selectOptions(values) {
    /** @type {HTMLInputElement | HTMLSelectElement | null} */
    let lastChanged = null;
    const current = this.getSelectedValues();
    values.forEach((value, index) => {
      if (value === undefined || value === current[index]) return;
      lastChanged = this.setOptionValue(index + 1, value) ?? lastChanged;
    });
    if (lastChanged) {
      /** @type {HTMLElement} */ (lastChanged).dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (this.dataset.skipUnavailable !== 'true') return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    const container = target.closest('[data-option-position]');
    const position = Number(/** @type {HTMLElement | null} */ (container)?.dataset.optionPosition);
    if (!position) return;

    const selected = this.getSelectedValues();
    selected[position - 1] = target.value;

    const exact = this.variants.find((variant) => variant.options.every((value, i) => value === selected[i]));
    if (exact?.available) return;

    const candidates = this.variants
      .filter((variant) => variant.available && variant.options[position - 1] === target.value)
      .map((variant) => ({
        variant,
        score: variant.options.reduce((score, value, i) => score + (value === selected[i] ? 1 : 0), 0),
      }))
      .sort((a, b) => b.score - a.score);

    const best = candidates[0]?.variant;
    if (!best) return;

    best.options.forEach((value, i) => {
      if (i !== position - 1 && value !== selected[i]) this.setOptionValue(i + 1, value);
    });
  };
}

/**
 * Adds "skip cart" (straight to checkout after Add to cart) and the secondary "Buy it now" button
 * on top of Horizon's product form.
 */
class ProBuyButtons extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit, true);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit, true);
    this.removeEventListener('click', this.#onClick);
  }

  get form() {
    return this.querySelector('form');
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const section = this.closest('.shopify-section');
    const invalidField = /** @type {HTMLElement | null} */ (section?.querySelector('pro-custom-field[data-invalid]'));
    if (invalidField) {
      event.preventDefault();
      event.stopPropagation();
      invalidField.querySelector('[data-pro-field-error]')?.removeAttribute('hidden');
      invalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      /** @type {HTMLElement | null} */ (invalidField.querySelector('input, textarea, select'))?.focus({ preventScroll: true });
      return;
    }

    let items = null;
    try {
      items = section && this.form ? collectCartItems(section, this.form) : null;
    } catch (error) {
      event.preventDefault();
      event.stopPropagation();
      this.#showError(String(/** @type {Error} */ (error).message || error));
      return;
    }

    if (!items && this.dataset.skipCart !== 'true') return;
    event.preventDefault();
    event.stopPropagation();
    const button = this.querySelector('[ref="addToCartButton"]');
    if (!items) {
      this.addAndCheckout(button);
      return;
    }
    button?.setAttribute('aria-busy', 'true');
    proAddToCart(items, { source: this, checkout: this.dataset.skipCart === 'true' })
      .then(() => this.querySelector('[ref="addToCartTextError"]')?.classList.add('hidden'))
      .catch((error) => this.#showError(error.message))
      .finally(() => button?.removeAttribute('aria-busy'));
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('[data-pro-buy-now]') : null);
    if (!button) return;
    event.preventDefault();
    this.addAndCheckout(button);
  };

  /** @param {Element | null} button */
  async addAndCheckout(button) {
    const form = this.form;
    if (!form || !(button instanceof HTMLButtonElement) || button.disabled) return;
    const section = this.closest('.shopify-section');
    if (section?.querySelector('pro-custom-field[data-invalid]')) {
      form.requestSubmit();
      return;
    }
    const items = section ? collectCartItems(section, form) : null;
    if (items) {
      button.setAttribute('aria-busy', 'true');
      proAddToCart(items, { source: this, checkout: true })
        .catch((error) => this.#showError(error.message))
        .finally(() => button.removeAttribute('aria-busy'));
      return;
    }

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      const data = await response.json();
      if (data.status) {
        this.#showError(data.description || data.message || 'Error');
        return;
      }
      window.location.href = this.dataset.checkoutUrl || '/checkout';
    } catch (error) {
      console.error(error);
      this.#showError(String(error));
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('[ref="addToCartTextError"]');
    if (!error) return;
    error.classList.remove('hidden');
    const text = error.childNodes[2];
    if (text) {
      text.textContent = message;
    } else {
      error.appendChild(document.createTextNode(message));
    }
  }
}

/**
 * Sticky Add to cart bar. Shows always or after scrolling past the main Add to cart button,
 * submits the main product form or scrolls to a target, and drives the main variant picker
 * from its dropdowns.
 */
class ProStickyAtc extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #pinnedInEditor = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);

    if (this.dataset.display === 'always') {
      this.#setVisible(true);
    } else {
      this.#observeMainButton();
    }

    this.#resizeObserver = new ResizeObserver(() => this.#updateHeight());
    this.#resizeObserver.observe(this);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onEditorSelect);
      document.addEventListener('shopify:block:deselect', this.#onEditorDeselect);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    document.removeEventListener('shopify:block:select', this.#onEditorSelect);
    document.removeEventListener('shopify:block:deselect', this.#onEditorDeselect);
    document.documentElement.classList.remove(STICKY_VISIBLE_CLASS);
  }

  #observeMainButton() {
    const section = this.closest('.shopify-section');
    const target =
      section?.querySelector('.pro-buy-buttons') ??
      section?.querySelector('pro-variant-picker') ??
      section?.querySelector('.pro-product__info');
    if (!target) {
      this.#setVisible(true);
      return;
    }

    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.#setVisible(scrolledPast || this.#pinnedInEditor);
    });
    this.#observer.observe(target);
  }

  /** @param {boolean} visible */
  #setVisible(visible) {
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', String(!visible));
    this.toggleAttribute('inert', !visible);
    document.documentElement.classList.toggle(STICKY_VISIBLE_CLASS, visible);
    this.#updateHeight();
  }

  #updateHeight() {
    document.documentElement.style.setProperty('--pro-sticky-atc-height', `${this.offsetHeight}px`);
  }

  get mainPicker() {
    return /** @type {ProVariantPicker | null} */ (this.closest('.shopify-section')?.querySelector('pro-variant-picker') ?? null);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-pro-sticky-button]') : null;
    if (!button || /** @type {HTMLButtonElement} */ (button).disabled) return;
    if (button.hasAttribute('data-pro-field-blocked')) {
      scrollToTarget(`#ProductInfo-${this.dataset.sectionId}`);
      return;
    }

    if (this.dataset.function === 'scroll') {
      scrollToTarget(this.dataset.scrollTarget || '');
      return;
    }

    const form = /** @type {HTMLFormElement | null} */ (document.getElementById(this.dataset.formId || ''));
    if (form) {
      form.requestSubmit();
    } else {
      scrollToTarget(`#ProductInfo-${this.dataset.sectionId}`);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const picker = this.mainPicker;
    if (!picker) return;

    if (select.hasAttribute('data-pro-sticky-variant')) {
      const option = select.selectedOptions[0];
      try {
        picker.selectOptions(JSON.parse(option?.dataset.options || '[]'));
      } catch {
        /* ignore malformed options */
      }
    } else if (select.dataset.proStickyOption) {
      const values = picker.getSelectedValues();
      values[Number(select.dataset.proStickyOption) - 1] = select.value;
      picker.selectOptions(values);
    }
  };

  /** @param {any} event */
  #onEditorSelect = (event) => {
    if (event.detail?.blockId !== this.dataset.blockId) return;
    this.#pinnedInEditor = true;
    this.#setVisible(true);
  };

  /** @param {any} event */
  #onEditorDeselect = (event) => {
    if (event.detail?.blockId !== this.dataset.blockId) return;
    this.#pinnedInEditor = false;
    if (this.dataset.display !== 'always') this.#setVisible(false);
  };
}

/**
 * Smooth scroll to a CSS selector or element id, leaving room for the sticky header.
 * @param {string} target
 */
export function scrollToTarget(target) {
  if (!target) return;
  let element = null;
  try {
    element = document.querySelector(target.startsWith('#') || target.startsWith('.') ? target : `#${CSS.escape(target)}`);
  } catch {
    element = null;
  }
  element ??= document.getElementById(target.replace(/^#/, ''));
  if (!element) return;

  const headerHeight =
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
  const top = element.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
  window.scrollTo({ top, behavior: 'smooth' });
}

/* ---------- Delegated behaviour: popups and scroll links ---------- */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const opener = target.closest('[data-pro-open-modal]');
  if (opener) {
    const dialog = document.getElementById(opener.getAttribute('data-pro-open-modal') || '');
    if (dialog instanceof HTMLDialogElement) {
      event.preventDefault();
      dialog.showModal();
      window.ProIcons?.refresh();
    }
    return;
  }

  const closer = target.closest('[data-pro-close-modal]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }

  // Click on the backdrop of a Pro modal closes it
  if (target instanceof HTMLDialogElement && target.matches('[data-pro-modal]')) {
    const rect = target.getBoundingClientRect();
    const event_ = /** @type {MouseEvent} */ (event);
    const inside =
      event_.clientX >= rect.left && event_.clientX <= rect.right && event_.clientY >= rect.top && event_.clientY <= rect.bottom;
    if (!inside) target.close();
    return;
  }

  const scrollLink = target.closest('[data-pro-scroll-to]');
  if (scrollLink) {
    event.preventDefault();
    scrollToTarget(scrollLink.getAttribute('data-pro-scroll-to') || '');
    return;
  }

  // Button links like "#shopify-section-xxx" scroll smoothly to the section on this page
  const anchor = /** @type {HTMLAnchorElement | null} */ (target.closest('a[data-pro-scroll-link]'));
  if (anchor) {
    const url = new URL(anchor.href, window.location.href);
    if (url.pathname === window.location.pathname && url.hash) {
      event.preventDefault();
      scrollToTarget(url.hash);
    }
    return;
  }

  const atcButton = /** @type {HTMLButtonElement | null} */ (target.closest('[data-pro-atc]'));
  if (atcButton && !atcButton.disabled) {
    event.preventDefault();
    const variantId = atcButton.dataset.variantId;
    if (variantId) {
      atcButton.setAttribute('aria-busy', 'true');
      proAddToCart([{ id: variantId, quantity: 1 }], { source: atcButton, checkout: atcButton.dataset.skipCart === 'true' })
        .catch((error) => console.warn('[pro] add to cart failed', error))
        .finally(() => atcButton.removeAttribute('aria-busy'));
    } else {
      const form = /** @type {HTMLFormElement | null} */ (document.querySelector('form[data-type="add-to-cart-form"]'));
      if (form) form.requestSubmit();
    }
  }
});

/** Keeps "test mode" popups open in the theme editor */
function openTestModeModals(root = document) {
  if (!window.Shopify?.designMode) return;
  for (const dialog of root.querySelectorAll('dialog[data-pro-test-open]')) {
    if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.show();
  }
}

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => openTestModeModals(/** @type {any} */ (event.target)));
  document.addEventListener('shopify:block:select', (event) => {
    const target = /** @type {any} */ (event.target);
    if (target instanceof Element) {
      const dialog = target.querySelector?.('dialog[data-pro-modal]');
      if (dialog instanceof HTMLDialogElement && dialog.hasAttribute('data-pro-test-open') && !dialog.open) dialog.show();
    }
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => openTestModeModals(), { once: true });
  } else {
    openTestModeModals();
  }
}

if (!customElements.get('pro-sync')) customElements.define('pro-sync', ProSync);
if (!customElements.get('pro-variant-picker')) customElements.define('pro-variant-picker', ProVariantPicker);
if (!customElements.get('pro-buy-buttons')) customElements.define('pro-buy-buttons', ProBuyButtons);
if (!customElements.get('pro-sticky-atc')) customElements.define('pro-sticky-atc', ProStickyAtc);

/* ==========================================================================
   Additional product information blocks
   ========================================================================== */

/** @param {number} cents @param {HTMLElement} el */
async function formatPrice(cents, el) {
  const { formatMoney } = await import('@theme/money-formatting');
  const host = /** @type {HTMLElement | null} */ (el.closest('[data-money-format]'));
  return formatMoney(Math.max(0, Math.round(cents)), host?.dataset.moneyFormat || '{{amount}}', host?.dataset.currency || 'USD');
}

/** @param {Element} root */
function readJson(root, selector, fallback) {
  try {
    return JSON.parse(root.querySelector(selector)?.textContent || '') ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * Picks the variant matching the values of a set of <select data-option-index> and, when skipping sold out
 * variants, falls back to an available one and disables impossible values.
 * @param {Array<any>} variants
 * @param {HTMLSelectElement[]} selects
 * @param {boolean} skipUnavailable
 * @param {number} changedIndex
 */
function resolveSelects(variants, selects, skipUnavailable, changedIndex = -1) {
  const values = () => selects.map((select) => select.value);
  let variant = variants.find((v) => v.options.every((value, i) => value === values()[i]));
  if (skipUnavailable && (!variant || !variant.available) && changedIndex >= 0) {
    const fallback = variants.find((v) => v.available && v.options[changedIndex] === selects[changedIndex]?.value);
    if (fallback) {
      fallback.options.forEach((value, i) => {
        if (selects[i]) selects[i].value = value;
      });
      variant = fallback;
    }
  }
  if (skipUnavailable) {
    selects.forEach((select, i) => {
      for (const option of select.options) {
        const test = values();
        test[i] = option.value;
        option.disabled = !variants.some((v) => v.available && v.options.every((value, j) => value === test[j]));
      }
    });
  }
  return variant;
}

/** Classic − / + quantity selector */
class ProQuantity extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input'));
      if (!target || !input) return;
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      let value = Number(input.value) || min;
      if (target.closest('[data-pro-qty-plus]')) value = Math.min(max, value + step);
      else if (target.closest('[data-pro-qty-minus]')) value = Math.max(min, value - step);
      else return;
      input.value = String(value);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

/** Quantity breaks with discounts and an optional variant picker per item */
class ProQuantityBreaks extends HTMLElement {
  /** @type {() => void} */
  #unsubscribe = () => {};
  #variantId = 0;

  connectedCallback() {
    this.data = readJson(this, 'script[data-pro-qb-variants]', { variants: [] });
    this.#variantId = this.data.selected;
    this.addEventListener('change', this.#onChange);
    this.#unsubscribe = onProductSelect(this, ({ resource }) => {
      if (!resource) return;
      this.#variantId = resource.id;
      for (const row of this.querySelectorAll('[data-pro-qb-row]')) this.#setRowVariant(/** @type {HTMLElement} */ (row), resource);
      if (this.dataset.updatePrices === 'true') this.#updatePrices(resource);
    });
    this.#renderRows();
  }

  disconnectedCallback() {
    this.#unsubscribe();
  }

  get quantity() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('.pro-break input:checked'));
    return checked ? Number(checked.value) : 1;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.type === 'radio') {
      const hidden = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-pro-qb-quantity]'));
      if (hidden) hidden.value = target.value;
      this.#renderRows();
      this.dispatchEvent(new CustomEvent('pro-quantity:change', { bubbles: true, detail: { quantity: this.quantity } }));
    } else if (target instanceof HTMLSelectElement) {
      const row = /** @type {HTMLElement | null} */ (target.closest('[data-pro-qb-row]'));
      if (row) this.#syncRow(row, Number(target.dataset.optionIndex));
    }
  };

  #renderRows() {
    const wrapper = /** @type {HTMLElement | null} */ (this.querySelector('[data-pro-qb-rows]'));
    const list = this.querySelector('[data-pro-qb-rows-list]');
    const template = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-pro-qb-row-template]'));
    if (!wrapper || !list || !template || this.dataset.variantSelectors !== 'true') return;

    const quantity = this.quantity;
    const show = quantity > 1 || this.dataset.selectorsOnOne === 'true';
    wrapper.hidden = !show;
    if (!show) {
      list.replaceChildren();
      return;
    }
    while (list.children.length < quantity) {
      const row = /** @type {HTMLElement} */ (template.content.firstElementChild?.cloneNode(true));
      list.appendChild(row);
      const current = this.data.variants.find((v) => v.id === this.#variantId);
      if (current) this.#setRowVariant(row, current);
    }
    while (list.children.length > quantity) list.lastElementChild?.remove();
    Array.from(list.children).forEach((row, i) => {
      const index = row.querySelector('.pro-qb-row__index');
      if (index) index.textContent = `#${i + 1}`;
    });
  }

  /** @param {HTMLElement} row @param {any} variant */
  #setRowVariant(row, variant) {
    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(row.querySelectorAll('select')));
    (variant.options || []).forEach((value, i) => {
      if (selects[i]) selects[i].value = value;
    });
    this.#syncRow(row, -1);
  }

  /** @param {HTMLElement} row @param {number} changedIndex */
  #syncRow(row, changedIndex) {
    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(row.querySelectorAll('select')));
    const variant = resolveSelects(this.data.variants, selects, this.dataset.skipUnavailable === 'true', changedIndex);
    row.dataset.variantId = variant ? String(variant.id) : '';
    row.classList.toggle('is-unavailable', !variant || !variant.available);
  }

  /** Items to add when every unit has its own variant, or null for a single variant */
  items() {
    const rows = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-pro-qb-row]')));
    if (rows.length === 0) return null;
    /** @type {Map<string, number>} */
    const counts = new Map();
    for (const row of rows) {
      const id = row.dataset.variantId;
      if (!id) throw new Error('Unavailable variant selected');
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    return [...counts].map(([id, quantity]) => ({ id, quantity }));
  }

  /** @param {any} variant */
  async #updatePrices(variant) {
    for (const card of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.pro-break[data-quantity]'))) {
      const qty = Number(card.dataset.quantity) || 1;
      const multiplier = parseFloat(card.dataset.multiplier || '1');
      const fixed = parseFloat(card.dataset.fixed || '0');
      const price = Math.max(0, Math.round(variant.price * qty * multiplier - fixed));
      const unitCompare = card.dataset.compareMode === 'compare_price' && variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
      const compare = unitCompare * qty;
      const saved = Math.max(0, compare - price);
      const values = {
        '[quantity]': String(qty),
        '[price_each]': await formatPrice(price / qty, this),
        '[compare_price_each]': await formatPrice(compare / qty, this),
        '[compare_price]': await formatPrice(compare, this),
        '[price]': await formatPrice(price, this),
        '[amount_saved_rounded]': await formatPrice(Math.ceil(saved / 100) * 100, this),
        '[amount_saved]': await formatPrice(saved, this),
      };
      for (const el of /** @type {NodeListOf<HTMLElement>} */ (card.querySelectorAll('[data-template]'))) {
        let text = el.dataset.template || '';
        for (const [key, value] of Object.entries(values)) text = text.split(key).join(value);
        el.textContent = text;
      }
      const comparePrice = card.querySelector('.pro-break__compare-price');
      if (comparePrice) comparePrice.toggleAttribute('hidden', !(compare > price));
    }
  }
}

/**
 * Shared behaviour for product lists with a variant picker per product
 * (upsells and the bundle offer): variant resolution, prices and images.
 */
class ProProductItems extends HTMLElement {
  /** @param {HTMLElement} item */
  variants(item) {
    if (!item.proVariants) item.proVariants = readJson(item, 'script[data-pro-upsell-variants]', []);
    return item.proVariants;
  }

  /** @param {HTMLElement} item @param {number} changedIndex */
  async syncItem(item, changedIndex = -1) {
    const selects = /** @type {HTMLSelectElement[]} */ (Array.from(item.querySelectorAll('select[data-option-index]')));
    if (selects.length === 0) return;
    const variant = resolveSelects(this.variants(item), selects, this.dataset.skipUnavailable === 'true', changedIndex);
    item.dataset.variantId = variant ? String(variant.id) : '';
    item.classList.toggle('is-unavailable', !variant || !variant.available);
    if (!variant) return;
    const image = /** @type {HTMLImageElement | null} */ (item.querySelector('img[data-pro-upsell-image]'));
    if (image && variant.image) {
      image.removeAttribute('srcset');
      image.src = variant.image;
    }
    if (this.dataset.updatePrices !== 'true') return;
    const multiplier = parseFloat(item.dataset.multiplier || '1');
    const fixed = parseFloat(item.dataset.fixed || '0');
    const price = Math.max(0, Math.round(variant.price * multiplier - fixed));
    const compare = variant.compare > variant.price ? variant.compare : variant.price;
    item.dataset.price = String(price);
    item.dataset.compare = String(compare);
    const priceEl = item.querySelector('[data-pro-upsell-price]');
    const compareEl = item.querySelector('[data-pro-upsell-compare]');
    if (priceEl) {
      priceEl.textContent = price === 0 && this.dataset.freeMode === 'text' ? this.dataset.freeText || 'FREE' : await formatPrice(price, this);
    }
    if (compareEl) compareEl.textContent = compare > price ? await formatPrice(compare, this) : '';
  }
}

/** Product upsells added together with the main product */
class ProUpsells extends ProProductItems {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    const url = this.dataset.recsUrl;
    if (url && !this.querySelector('[data-pro-upsell]')) this.#loadRecommendations(url);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    const item = target instanceof Element ? /** @type {HTMLElement | null} */ (target.closest('[data-pro-upsell]')) : null;
    if (!item) return;
    if (target instanceof HTMLSelectElement) this.syncItem(item, Number(target.dataset.optionIndex));
    if (target instanceof HTMLInputElement && target.matches('[data-pro-upsell-checkbox]')) item.classList.toggle('is-selected', target.checked);
  };

  /** Clicking the whole container toggles the upsell when configured */
  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (this.dataset.toggleElement !== 'container') return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest('select, a, label, input, button')) return;
    const item = target.closest('[data-pro-upsell]');
    const checkbox = /** @type {HTMLInputElement | null} */ (item?.querySelector('[data-pro-upsell-checkbox]'));
    if (checkbox && !checkbox.disabled) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /** @param {string} url */
  #loadRecommendations(url) {
    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const fresh = html.querySelector(`pro-upsells[data-block-id="${CSS.escape(this.dataset.blockId || '')}"] [data-pro-upsells-list]`);
        const list = this.querySelector('[data-pro-upsells-list]');
        if (fresh && list) {
          list.innerHTML = fresh.innerHTML;
          window.ProIcons?.refresh();
        }
      })
      .catch((error) => console.warn('[pro] upsell recommendations failed', error));
  }

  items() {
    return Array.from(this.querySelectorAll('[data-pro-upsell]'))
      .filter((item) => /** @type {HTMLInputElement | null} */ (item.querySelector('[data-pro-upsell-checkbox]'))?.checked)
      .map((item) => ({ id: /** @type {HTMLElement} */ (item).dataset.variantId, quantity: 1 }))
      .filter((item) => item.id);
  }
}

/** Premade bundle offer */
class ProBundleOffer extends ProProductItems {
  connectedCallback() {
    this.addEventListener('change', async (event) => {
      const target = event.target;
      const item = target instanceof Element ? /** @type {HTMLElement | null} */ (target.closest('[data-pro-offer-item]')) : null;
      if (item && target instanceof HTMLSelectElement) {
        await this.syncItem(item, Number(target.dataset.optionIndex));
        this.#updateTotal();
      }
    });
    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-pro-offer-submit]') : null;
      if (!button) return;
      let items = this.items();
      if (this.dataset.includeUpsells === 'true') {
        const section = this.closest('.shopify-section');
        for (const upsells of section?.querySelectorAll('pro-upsells') ?? []) items = items.concat(/** @type {ProUpsells} */ (upsells).items());
      }
      const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-pro-offer-error]'));
      button.setAttribute('aria-busy', 'true');
      proAddToCart(/** @type {any} */ (items), { source: this })
        .then(() => error?.setAttribute('hidden', ''))
        .catch((e) => {
          if (error) {
            error.textContent = e.message;
            error.removeAttribute('hidden');
          }
        })
        .finally(() => button.removeAttribute('aria-busy'));
    });
  }

  async #updateTotal() {
    if (this.dataset.updatePrices !== 'true') return;
    let total = 0;
    let compare = 0;
    for (const item of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-pro-offer-item]'))) {
      total += parseFloat(item.dataset.price || '0');
      compare += parseFloat(item.dataset.compare || '0');
    }
    total = Math.max(0, Math.round(total * parseFloat(this.dataset.sectionMultiplier || '1') - parseFloat(this.dataset.sectionFixed || '0')));
    const totalEl = this.querySelector('[data-pro-offer-total]');
    const compareEl = this.querySelector('[data-pro-offer-total-compare]');
    if (totalEl) totalEl.textContent = await formatPrice(total, this);
    if (compareEl) compareEl.textContent = compare > total ? await formatPrice(compare, this) : '';
  }

  items() {
    return Array.from(this.querySelectorAll('[data-pro-offer-item]'))
      .map((item) => ({ id: /** @type {HTMLElement} */ (item).dataset.variantId, quantity: 1 }))
      .filter((item) => item.id);
  }
}

/** Gifts unlocked by the quantity of the main product; keeps them in the cart automatically */
class ProGifts extends HTMLElement {
  #busy = false;

  connectedCallback() {
    const section = this.closest('.shopify-section');
    section?.addEventListener('change', () => this.#renderLocks());
    section?.addEventListener('pro-quantity:change', () => this.#renderLocks());
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#renderLocks();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  get gifts() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-pro-gift]')));
  }

  #selectedQuantity() {
    const section = this.closest('.shopify-section');
    const input = /** @type {HTMLInputElement | null} */ (section?.querySelector('[data-pro-qb-quantity], input[name="quantity"]'));
    return Number(input?.value) || 1;
  }

  #renderLocks() {
    const quantity = this.#selectedQuantity();
    for (const gift of this.gifts) gift.classList.toggle('is-unlocked', quantity >= Number(gift.dataset.min || 1));
  }

  /** @param {any} event */
  #onCartUpdate = (event) => {
    if (this.#busy || event.detail?.source === 'pro-gifts') return;
    event.promise?.then(() => this.#reconcile()).catch(() => {});
  };

  async #reconcile() {
    this.#busy = true;
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const productId = Number(this.dataset.productId);
      const giftIds = new Set(this.gifts.map((gift) => Number(gift.dataset.variantId)));
      const mainQuantity = cart.items
        .filter((item) => item.product_id === productId && !giftIds.has(item.variant_id))
        .reduce((sum, item) => sum + item.quantity, 0);

      /** @type {Record<string, number>} */
      const updates = {};
      const toAdd = [];
      for (const gift of this.gifts) {
        const variantId = Number(gift.dataset.variantId);
        const unlocked = mainQuantity >= Number(gift.dataset.min || 1);
        const inCart = cart.items.find((item) => item.variant_id === variantId);
        if (unlocked && !inCart) toAdd.push({ id: variantId, quantity: 1, properties: { _gift: 'true' } });
        if (!unlocked && inCart) updates[String(variantId)] = 0;
        if (unlocked && inCart && inCart.quantity !== 1) updates[String(variantId)] = 1;
      }
      if (Object.keys(updates).length) {
        await fetch(Theme.routes.cart_update_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
      }
      if (toAdd.length) await proAddToCart(toAdd, { source: this });
      else if (Object.keys(updates).length) await proRefreshCart(this);
    } catch (error) {
      console.warn('[pro] gifts update failed', error);
    } finally {
      this.#busy = false;
    }
  }
}

/** Custom product field: required validation that disables the Add to cart buttons */
class ProCustomField extends HTMLElement {
  connectedCallback() {
    this.addEventListener('input', () => this.#validate(false));
    this.addEventListener('change', () => this.#validate(false));
    this.addEventListener('focusout', () => this.#validate(true));
    this.#validate(false);
  }

  get valid() {
    if (this.dataset.required !== 'true') return true;
    const controls = /** @type {Array<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>} */ (Array.from(this.querySelectorAll('input, textarea, select')));
    if (controls.some((c) => c instanceof HTMLInputElement && c.type === 'radio')) {
      return controls.some((c) => /** @type {HTMLInputElement} */ (c).checked);
    }
    return controls.every((c) => String(c.value).trim() !== '');
  }

  /** @param {boolean} showError */
  #validate(showError) {
    const valid = this.valid;
    this.toggleAttribute('data-invalid', !valid);
    const error = this.querySelector('[data-pro-field-error]');
    if (error && (showError || valid)) error.toggleAttribute('hidden', valid);
    updateRequiredFields(this.closest('.shopify-section'));
  }
}

/** Disables Add to cart buttons while a required custom field is empty and shows its message */
function updateRequiredFields(section) {
  if (!section) return;
  const invalid = /** @type {HTMLElement | null} */ (section.querySelector('pro-custom-field[data-invalid]'));
  const buttons = section.querySelectorAll('[ref="addToCartButton"], [data-pro-buy-now], [data-pro-sticky-button]');
  for (const button of buttons) {
    const isSticky = button.hasAttribute('data-pro-sticky-button');
    const block = invalid && (!isSticky || invalid.dataset.stickyError === 'true');
    button.toggleAttribute('data-pro-field-blocked', Boolean(block));
    const label = button.querySelector('.pro-btn__text');
    if (label) {
      if (block) {
        if (!label.dataset.original) label.dataset.original = label.textContent || '';
        label.textContent = invalid?.dataset.atcError || label.dataset.original;
      } else if (label.dataset.original) {
        label.textContent = label.dataset.original;
        delete label.dataset.original;
      }
    }
  }
}

/** Complementary products: loaded from the recommendations endpoint */
class ProComplementary extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url || this.dataset.loaded) return;
    this.dataset.loaded = 'true';
    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const fresh = html.querySelector(`pro-complementary[data-block-id="${CSS.escape(this.dataset.blockId || '')}"]`);
        if (fresh && fresh.innerHTML.trim()) {
          this.innerHTML = fresh.innerHTML;
          this.hidden = false;
          window.ProIcons?.refresh();
        }
      })
      .catch((error) => console.warn('[pro] complementary products failed', error));
  }
}

/** Replaces [start_date] / [end_date] with dates N days from today */
class ProDeliveryDate extends HTMLElement {
  connectedCallback() {
    const days = (this.dataset.days || '').split(',').map((s) => s.trim());
    const months = (this.dataset.months || '').split(',').map((s) => s.trim());
    const format = this.dataset.format || 'mm_dd';
    const start = this.#format(this.#addDays(Number(this.dataset.min) || 0), format, days, months);
    const end = this.#format(this.#addDays(Number(this.dataset.max) || 0), format, days, months);
    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);
    /** @type {Text[]} */
    const nodes = [];
    while (walker.nextNode()) nodes.push(/** @type {Text} */ (walker.currentNode));
    for (const node of nodes) {
      if (node.nodeValue?.includes('[start_date]') || node.nodeValue?.includes('[end_date]')) {
        node.nodeValue = node.nodeValue.split('[start_date]').join(start).split('[end_date]').join(end);
      }
    }
  }

  /** @param {number} n */
  #addDays(n) {
    const date = new Date();
    date.setDate(date.getDate() + n);
    return date;
  }

  /** @param {Date} date @param {string} format @param {string[]} days @param {string[]} months */
  #format(date, format, days, months) {
    const dayName = days[(date.getDay() + 6) % 7] || '';
    const monthName = months[date.getMonth()] || '';
    const d = date.getDate();
    const dd = String(d).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const suffix = d % 10 === 1 && d !== 11 ? 'st' : d % 10 === 2 && d !== 12 ? 'nd' : d % 10 === 3 && d !== 13 ? 'rd' : 'th';
    switch (format) {
      case 'day_mm_dd': return `${dayName}, ${monthName} ${d}${suffix}`;
      case 'day_dd_mm': return `${dayName}, ${d}. ${monthName}`;
      case 'dd_mm': return `${d}. ${monthName}`;
      case 'dd_mm_no_dot': return `${d} ${monthName}`;
      case 'day_dd_mm_numeric': return `${dayName}, ${dd}. ${mm}.`;
      case 'dd_mm_numeric': return `${dd}. ${mm}.`;
      default: return `${monthName} ${d}${suffix}`;
    }
  }
}

/* Share button and clickable discount (delegated) */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const share = /** @type {HTMLElement | null} */ (target?.closest('[data-pro-share]'));
  if (share) {
    const url = share.dataset.url || window.location.href;
    if (navigator.share) {
      navigator.share({ url, title: share.dataset.title }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url).then(() => {
        const copied = share.querySelector('.pro-share__copied');
        copied?.removeAttribute('hidden');
        setTimeout(() => copied?.setAttribute('hidden', ''), 2000);
      });
    }
    return;
  }
  const discount = /** @type {HTMLElement | null} */ (target?.closest('[data-pro-discount]'));
  if (discount && !discount.classList.contains('is-applied')) {
    const code = discount.dataset.proDiscount || '';
    fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' })
      .then(() => {
        discount.classList.add('is-applied');
        try {
          localStorage.setItem(`pro-discount-${code}`, '1');
        } catch {
          /* storage unavailable */
        }
      })
      .catch((error) => console.warn('[pro] discount failed', error));
  }
});

function restoreDiscounts(root = document) {
  for (const button of root.querySelectorAll('[data-pro-discount]')) {
    try {
      if (localStorage.getItem(`pro-discount-${/** @type {HTMLElement} */ (button).dataset.proDiscount}`)) button.classList.add('is-applied');
    } catch {
      /* storage unavailable */
    }
  }
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => restoreDiscounts(), { once: true });
else restoreDiscounts();

/**
 * Items for the main Add to cart: the main bundle offer, per-item variants of quantity breaks and
 * selected upsells. Returns null when the regular single-variant add is enough.
 * @param {Element} section
 * @param {HTMLFormElement} form
 */
export function collectCartItems(section, form) {
  const formData = new FormData(form);
  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
  }
  const withProperties = (/** @type {any} */ item) => (Object.keys(properties).length ? { ...item, properties } : item);

  let main = null;
  const offer = /** @type {ProBundleOffer | null} */ (section.querySelector('pro-bundle-offer[data-type="main"]'));
  if (offer) main = offer.items().map(withProperties);
  if (!main) {
    const breaks = /** @type {ProQuantityBreaks | null} */ (section.querySelector('pro-quantity-breaks'));
    const rows = breaks?.items();
    if (rows) main = rows.map(withProperties);
  }

  const upsells = Array.from(section.querySelectorAll('pro-upsells')).flatMap((el) => /** @type {ProUpsells} */ (el).items());
  if (!main && upsells.length === 0) return null;
  if (!main) {
    main = [withProperties({ id: formData.get('id'), quantity: Number(formData.get('quantity')) || 1 })];
  }
  return [...main, ...upsells];
}

if (!customElements.get('pro-quantity')) customElements.define('pro-quantity', ProQuantity);
if (!customElements.get('pro-quantity-breaks')) customElements.define('pro-quantity-breaks', ProQuantityBreaks);
if (!customElements.get('pro-upsells')) customElements.define('pro-upsells', ProUpsells);
if (!customElements.get('pro-bundle-offer')) customElements.define('pro-bundle-offer', ProBundleOffer);
if (!customElements.get('pro-gifts')) customElements.define('pro-gifts', ProGifts);
if (!customElements.get('pro-custom-field')) customElements.define('pro-custom-field', ProCustomField);
if (!customElements.get('pro-complementary')) customElements.define('pro-complementary', ProComplementary);
if (!customElements.get('pro-delivery-date')) customElements.define('pro-delivery-date', ProDeliveryDate);

/**
 * <pro-stock-counter>: "Only X left" for the selected variant from lines "option values = quantity".
 * The most specific line wins (more matching values), "*" is the fallback, 0 shows the sold out text.
 */
class ProStockCounter extends HTMLElement {
  /** @type {() => void} */
  #unsubscribe = () => {};

  connectedCallback() {
    this.rules = this.#parse(this.querySelector('[data-pro-stock-list]')?.textContent || '');
    /** @type {Array<{options: string[], available: boolean}>} */
    this.variants = readJson(this, '[data-pro-stock-variants]', []);
    this.section = this.closest('.shopify-section');
    // Instant update when an option is clicked (no waiting for the variant to load from the server)
    this.section?.addEventListener('change', this.#onOptionChange);
    // Confirmation once the server returns the new variant (also covers variant changes from other places)
    this.#unsubscribe = onProductSelect(this, ({ resource }) => {
      if (resource?.options) this.render(resource.options);
    });
    this.render(this.#selectedOptions() ?? readJson(this, '[data-pro-stock-variant]', []));
  }

  disconnectedCallback() {
    this.section?.removeEventListener('change', this.#onOptionChange);
    this.#unsubscribe();
  }

  /** @param {Event} event */
  #onOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.matches('[data-option-position]')) return;
    const options = this.#selectedOptions();
    if (options) this.render(options);
  };

  /** Option values currently chosen in the variant picker of this section, by option position */
  #selectedOptions() {
    const picker = this.section?.querySelector('variant-picker');
    if (!picker) return null;
    /** @type {string[]} */
    const values = [];
    for (const field of picker.querySelectorAll('input[type="radio"][data-option-position]:checked, select[data-option-position]')) {
      const position = Number(/** @type {HTMLElement} */ (field).dataset.optionPosition);
      if (position > 0) values[position - 1] = /** @type {HTMLInputElement | HTMLSelectElement} */ (field).value;
    }
    return values.length ? values : null;
  }

  /** @param {string} text */
  #parse(text) {
    return text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.includes('='))
      .map((line) => {
        const index = line.lastIndexOf('=');
        const key = line.slice(0, index).trim();
        const quantity = Number(line.slice(index + 1).trim());
        const values = key === '*' ? [] : key.split(/\s*[\/|,]\s*/).map((v) => v.trim().toLowerCase()).filter(Boolean);
        return { values, quantity, wildcard: key === '*' };
      })
      .filter((rule) => Number.isFinite(rule.quantity) && (rule.wildcard || rule.values.length > 0));
  }

  /** @param {string[]} options - Selected variant option values */
  render(options) {
    const selected = (options || []).map((v) => String(v ?? '').trim().toLowerCase());
    let best = null;
    for (const rule of this.rules) {
      if (rule.wildcard) {
        if (!best) best = rule;
        continue;
      }
      if (rule.values.every((value) => selected.includes(value)) && (!best || best.wildcard || rule.values.length > best.values.length)) {
        best = rule;
      }
    }

    // Sold out in Shopify, or a combination of options that doesn't exist
    let unavailable = false;
    if (this.variants.length) {
      const variant = this.variants.find((v) => v.options.every((value, i) => String(value).trim().toLowerCase() === selected[i]));
      unavailable = !variant || !variant.available;
    }
    const soldOut = unavailable || (best !== null && best.quantity <= 0);

    if (!best && !soldOut) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    const label = this.querySelector('[data-pro-stock-label]');
    const count = this.querySelector('[data-pro-stock-count]');
    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-pro-stock-bar]'));
    const fill = /** @type {HTMLElement | null} */ (this.querySelector('[data-pro-stock-fill]'));

    this.classList.toggle('is-sold-out', soldOut);
    if (soldOut) {
      if (label) label.textContent = this.dataset.soldOut || 'Sold Out';
      if (count) count.textContent = label ? '' : this.dataset.soldOut || 'Sold Out';
      if (bar) bar.hidden = true;
      return;
    }
    const quantity = best ? best.quantity : 0;
    if (label) label.textContent = this.dataset.label || '';
    if (count) count.textContent = (this.dataset.count || '').split('[quantity]').join(String(quantity));
    if (bar && fill) {
      bar.hidden = false;
      const max = Number(this.dataset.max) || 10;
      fill.style.width = `${Math.min(100, Math.max(5, Math.round((quantity / max) * 100)))}%`;
    }
  }
}

if (!customElements.get('pro-stock-counter')) customElements.define('pro-stock-counter', ProStockCounter);
